import React, { useCallback, useEffect, useRef, useState } from 'react';
import { T } from '../designTokens';

/**
 * Dismissible banner shown after a purchase completes.
 * Two call sites want two different container treatments — a rounded card
 * with bottom margin inline in the report body, or a full-width bar under
 * the header — so `variant` picks the container style while the copy,
 * escape-key dismissal, and timed-dismiss behavior stay shared.
 *
 * The copy says what the purchase did. A purchase made from a locked full
 * audit spends its new credit unlocking that report (#1762), so it reads
 * "Report unlocked" and claims no credits. Every other purchase reads
 * "Credits added", and per #878 never claims an unlock: `reportUnlocked` is
 * true only when PaymentSuccessPage saw the unlock succeed.
 */
export default function PostPurchaseBanner({
  variant,
  reportUnlocked = false,
  onDismiss,
}: {
  variant: 'card' | 'bar';
  reportUnlocked?: boolean;
  onDismiss: () => void;
}) {
  const [visible, setVisible] = useState(true);
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (dismissTimer.current !== null) clearTimeout(dismissTimer.current);
    };
  }, []);

  const dismiss = useCallback(() => {
    setVisible(false);
    // Brief delay to allow fade-out before unmounting.
    dismissTimer.current = setTimeout(onDismiss, 100);
  }, [onDismiss]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [dismiss]);

  const containerStyle: React.CSSProperties =
    variant === 'card'
      ? {
          backgroundColor: T.successBg,
          border: `1px solid ${T.successBorder}`,
          borderRadius: '6px',
          padding: '12px 16px',
          margin: '0 0 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          opacity: visible ? 1 : 0,
          transition: 'opacity 100ms',
          fontFamily: T.fontBody,
        }
      : {
          backgroundColor: T.successBg,
          borderBottom: `1px solid ${T.successBorder}`,
          padding: '12px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          opacity: visible ? 1 : 0,
          transition: 'opacity 100ms ease',
        };

  return (
    <div role="status" aria-label="Purchase confirmation" style={containerStyle}>
      <span
        style={{
          fontSize: '14px',
          fontWeight: 500,
          color: T.success,
          fontFamily: T.fontBody,
        }}
      >
        {reportUnlocked ? 'Report unlocked ✔' : 'Credits added ✔'}
      </span>
      <button
        onClick={dismiss}
        aria-label={variant === 'card' ? 'Dismiss' : 'Dismiss payment confirmation'}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: T.success,
          fontSize: '18px',
          lineHeight: 1,
          padding: variant === 'card' ? '4px' : '0 4px',
          minHeight: '44px',
          minWidth: '44px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        ×
      </button>
    </div>
  );
}
